import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Combobox } from '../../../../components/ui/combobox';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { PurchaseOrderLine, PurchaseOrderLinePayload } from '../../../../types/procurement.types';
import { Product } from '../../../../types/stock.types';
import { money } from '../procurement-ui';

/**
 * The line editor shared by the create form and the detail page's edit dialog.
 *
 * A line is either a Product Library line (picked from the library, which carries the SKU,
 * the UoM and the only price hint the library has — `replacementValue`) or a custom line
 * with a free-text description and no product record. Both are legal on a purchase order;
 * AP prices the custom ones by hand.
 *
 * `quantityReceived` rides on the draft because the server refuses to drop a line's ordered
 * quantity below what has already been received. The form never has any; the detail dialog
 * does, and shows the guard rather than letting the save fail.
 */

export interface PurchaseOrderLineDraft {
    /** Stable row identity — `line-<id>` for a saved line, `new-<n>` for an unsaved row. */
    key: string;
    /**
     * The saved line's id, null for a row that has never been persisted. The API matches a
     * supplied line by this id — without it every line in a save reads as an ADD and the
     * order's existing lines as removals, which is what made deleting a line fail.
     */
    id: number | null;
    productId: number | null;
    description: string;
    uom: string;
    sku: string | null;
    quantityOrdered: string;
    unitPrice: string;
    /** Already received against this line; 0 for anything not yet issued. */
    quantityReceived: number;
    /**
     * A receipt line — posted, or still being captured — already claims this row. The API
     * refuses to delete such a line, so the editor does not offer to.
     */
    hasReceipts: boolean;
    isCustom: boolean;
}

/** Why a line's delete button is off: the same rule the API enforces, in its words. */
const REMOVAL_BLOCKED_HINT =
    'This line has receipts against it and cannot be removed. Leave it in place and close ' +
    'the order short instead.';

/** Row keys only have to be stable within one editing session. */
let lineKeySeq = 0;

export const emptyLineDraft = (isCustom: boolean): PurchaseOrderLineDraft => ({
    key: `new-${++lineKeySeq}`,
    id: null,
    productId: null,
    description: '',
    uom: 'ea',
    sku: null,
    quantityOrdered: '',
    unitPrice: '',
    quantityReceived: 0,
    hasReceipts: false,
    isCustom,
});

/**
 * `hasReceipts` comes from the caller because only the order payload knows which lines a
 * receipt already claims — the create form has none, so it keeps the default.
 */
export const lineDraftFromLine = (
    line: PurchaseOrderLine,
    hasReceipts = false,
): PurchaseOrderLineDraft => ({
    key: `line-${line.id}`,
    id: line.id,
    productId: line.productId,
    description: line.description,
    uom: line.uom,
    sku: line.sku,
    quantityOrdered: String(line.quantityOrdered),
    unitPrice: String(line.unitPrice),
    quantityReceived: line.quantityReceived,
    hasReceipts,
    isCustom: line.isCustom,
});

export const lineDraftTotal = (line: PurchaseOrderLineDraft) =>
    (Number(line.quantityOrdered) || 0) * (Number(line.unitPrice) || 0);

export const orderDraftTotal = (lines: PurchaseOrderLineDraft[]) =>
    lines.reduce((sum, line) => sum + lineDraftTotal(line), 0);

/** What the API is sent: a line needs a description and a quantity to be a line at all. */
export const lineDraftPayloads = (lines: PurchaseOrderLineDraft[]): PurchaseOrderLinePayload[] =>
    lines
        .filter((line) => Boolean(line.description.trim()) && Number(line.quantityOrdered) > 0)
        .map((line) => ({
            // Carried so the API updates this row in place; null on a row the user just added.
            id: line.id,
            productId: line.productId,
            description: line.description.trim(),
            uom: line.uom.trim() || 'ea',
            sku: line.sku,
            quantityOrdered: Number(line.quantityOrdered),
            unitPrice: Number(line.unitPrice) || 0,
        }));

/** The first reason this row cannot be saved, or null when it is fine. */
export const lineDraftIssue = (line: PurchaseOrderLineDraft): string | null => {
    if (!line.description.trim()) {
        return line.isCustom ? 'Describe the item on this custom line.' : 'Pick a product.';
    }
    const quantity = Number(line.quantityOrdered);
    if (!Number.isFinite(quantity) || quantity <= 0) return 'Quantity must be greater than 0.';
    if (quantity < line.quantityReceived) {
        return `Already received ${line.quantityReceived} — the ordered quantity cannot drop below that.`;
    }
    const price = Number(line.unitPrice);
    if (!Number.isFinite(price) || price < 0) return 'Unit price cannot be negative.';
    return null;
};

/** True when nothing on the order can be saved yet — the pages disable their buttons on it. */
export const orderDraftIssue = (lines: PurchaseOrderLineDraft[]): string | null => {
    if (!lines.some((line) => line.description.trim() || Number(line.quantityOrdered) > 0)) {
        return 'Add at least one line.';
    }
    for (const line of lines) {
        const issue = lineDraftIssue(line);
        if (issue && (line.description.trim() || Number(line.quantityOrdered) > 0)) return issue;
    }
    return null;
};

interface Props {
    lines: PurchaseOrderLineDraft[];
    onChange: (lines: PurchaseOrderLineDraft[]) => void;
    /** Active Product Library items — the picker is a search over these. */
    products: Product[];
    disabled?: boolean;
}

export default function PurchaseOrderLineEditor({ lines, onChange, products, disabled = false }: Props) {
    const update = (key: string, patch: Partial<PurchaseOrderLineDraft>) =>
        onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));

    const remove = (key: string) => {
        const remaining = lines.filter((line) => line.key !== key);
        onChange(remaining.length ? remaining : [emptyLineDraft(false)]);
    };

    /** Picking from the library fills SKU, UoM and — when nothing is priced yet — the
        replacement value, which is the only price the Product Library holds. */
    const pickProduct = (line: PurchaseOrderLineDraft, value: string | undefined) => {
        const product = products.find((p) => String(p.id) === value);
        if (!product) {
            update(line.key, { productId: null, description: '', sku: null });
            return;
        }
        update(line.key, {
            productId: product.id,
            isCustom: false,
            description: product.name,
            sku: product.sku,
            uom: product.uom,
            unitPrice: line.unitPrice === '' && product.replacementValue !== null
                ? String(product.replacementValue)
                : line.unitPrice,
        });
    };

    const productOptions = products.map((product) => ({
        label: `${product.name} (${product.sku})`,
        value: String(product.id),
        subtitle: product.category,
    }));

    return (
        <div className="space-y-3">
            {lines.map((line, index) => {
                const issue = lineDraftIssue(line);
                const dirty = Boolean(line.description.trim() || Number(line.quantityOrdered) > 0);
                const received = line.quantityReceived > 0;
                const removalBlocked = received || line.hasReceipts;
                return (
                    <div key={line.key} className="rounded-lg border border-border p-3 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="text-xs font-medium text-muted-foreground">
                                    Line {index + 1}
                                </span>
                                <span className="text-[10px] font-medium text-muted-foreground bg-muted rounded px-1.5 py-0.5 whitespace-nowrap">
                                    {line.isCustom ? 'Custom line' : 'Product Library'}
                                </span>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Remove line ${index + 1}`}
                                disabled={disabled || lines.length === 1 || removalBlocked}
                                title={removalBlocked ? REMOVAL_BLOCKED_HINT : undefined}
                                onClick={() => remove(line.key)}
                            >
                                <Trash2 size={16} />
                            </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 md:grid-cols-12">
                            <div className="col-span-2 md:col-span-5">
                                <Label htmlFor={`line-item-${line.key}`}>
                                    {line.isCustom ? 'Description' : 'Product'}
                                </Label>
                                {line.isCustom ? (
                                    <Input
                                        id={`line-item-${line.key}`}
                                        value={line.description}
                                        disabled={disabled}
                                        placeholder="e.g. Freight to site — Wollongong"
                                        onChange={(e) => update(line.key, { description: e.target.value })}
                                    />
                                ) : (
                                    <Combobox
                                        options={productOptions}
                                        value={line.productId ? String(line.productId) : undefined}
                                        onChange={(value) => pickProduct(line, value)}
                                        placeholder={products.length ? 'Select a product' : 'No active products'}
                                        searchPlaceholder="Search the Product Library..."
                                        emptyMessage="No products found."
                                    />
                                )}
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor={`line-uom-${line.key}`}>UoM</Label>
                                <Input
                                    id={`line-uom-${line.key}`}
                                    value={line.uom}
                                    disabled={disabled}
                                    placeholder="ea"
                                    onChange={(e) => update(line.key, { uom: e.target.value })}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor={`line-qty-${line.key}`}>Quantity</Label>
                                <Input
                                    id={`line-qty-${line.key}`}
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    step="any"
                                    value={line.quantityOrdered}
                                    disabled={disabled}
                                    onChange={(e) => update(line.key, { quantityOrdered: e.target.value })}
                                />
                            </div>
                            <div className="md:col-span-3">
                                <Label htmlFor={`line-price-${line.key}`}>Unit price (ex GST)</Label>
                                <Input
                                    id={`line-price-${line.key}`}
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    step="0.01"
                                    value={line.unitPrice}
                                    disabled={disabled}
                                    placeholder="0.00"
                                    onChange={(e) => update(line.key, { unitPrice: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                            <span className="text-xs text-muted-foreground">
                                Line total{' '}
                                <span className="font-medium text-foreground tabular-nums">
                                    {money(lineDraftTotal(line))}
                                </span>
                            </span>
                            {removalBlocked && (
                                <span className="text-xs text-muted-foreground">
                                    {received
                                        ? `${line.quantityReceived} already received — cannot be removed`
                                        : 'A receipt is logged against this line — cannot be removed'}
                                </span>
                            )}
                        </div>

                        {dirty && issue && (
                            <p className="text-xs text-amber-800">{issue}</p>
                        )}
                    </div>
                );
            })}

            <div className="flex flex-wrap gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    onClick={() => onChange([...lines, emptyLineDraft(false)])}
                >
                    <Plus size={14} className="mr-1.5" />Add product line
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    onClick={() => onChange([...lines, emptyLineDraft(true)])}
                >
                    <Plus size={14} className="mr-1.5" />Add custom line
                </Button>
            </div>
            <p className="text-xs text-muted-foreground">
                A custom line has no Product Library record — Accounts Payable prices it by hand.
            </p>
        </div>
    );
}
